import {
  type CoachConfigurationSnapshot,
  type CoachQueueActionRequest,
  isCoachConfigurationSnapshot,
} from "@billiard/contracts";

export type CoachRuntimeOperationCommand =
  | {
      kind: "CHECK_IN";
      coachId: string;
      reason?: string | null;
      idempotencyKey: string;
      label: string;
    }
  | {
      kind: "CHECK_OUT";
      coachId: string;
      reason: string | null;
      idempotencyKey: string;
      label: string;
    }
  | { kind: "QUEUE"; input: CoachQueueActionRequest; label: string }
  | {
      kind: "TRANSITION";
      serviceId: string;
      action: "START" | "COMPLETE" | "END_EARLY" | "CANCEL" | "TERMINATE_ABNORMAL";
      reason: string | null;
      idempotencyKey: string;
      label: string;
    }
  | {
      kind: "ASSIGN";
      sessionId: string;
      coachId: string | null;
      assignmentType: "ROTATION" | "REQUESTED";
      serviceRuleId: string;
      reason?: string | null;
      idempotencyKey: string;
      label: string;
    };

export interface CoachConfigurationSaveCommand {
  kind: "SAVE_LEVEL" | "SAVE_RULE" | "SAVE_PRICE";
  configuration: CoachConfigurationSnapshot;
  idempotencyKey: string;
  label: string;
}

export type CoachRecoverableOperationCommand =
  | CoachRuntimeOperationCommand
  | CoachConfigurationSaveCommand;

export type CoachOperationRecoveryState = "CLAIMED" | "OUTCOME_UNKNOWN";

export interface CoachOperationRecovery {
  ownerIdentityKey: string;
  command: CoachRecoverableOperationCommand;
  state: CoachOperationRecoveryState;
}

export type CoachOperationRecoveryView =
  | { status: "NONE" }
  | { status: "OWNED"; recovery: CoachOperationRecovery }
  | { status: "OTHER_OPERATOR" }
  | { status: "UNAVAILABLE" };

interface RequestFailureShape {
  code?: unknown;
  statusCode?: unknown;
}

interface StorageAccess {
  browser: boolean;
  storage: Storage | null;
}

const definitelyStoppedBeforeCoachOperation = new Set([
  "AUTH_REQUIRED",
  "AUTH_CREDENTIAL_CONFLICT",
  "AUTHORITY_EXPIRED",
  "CSRF_REJECTED",
  "DEMO_AUTH_DISABLED",
  "DEMO_MODE_REQUIRED",
  "FORBIDDEN",
  "LEGACY_OPERATOR_SCOPE_GATE_MISMATCH",
  "MOBILE_SESSION_ACTIVATION_REQUIRED",
  "NATIVE_SECURE_SESSION_UNAVAILABLE",
  "NATIVE_UPDATE_REQUIRED",
  "ORIGIN_REJECTED",
  "REQUEST_VALIDATION_FAILED",
  "SHIFT_SESSION_NOT_STARTED",
]);

const unknownCoachOperationCodes = new Set([
  "COACH_CONFIGURATION_CONFIRMATION_PENDING",
  "COACH_OPERATION_RESULT_UNKNOWN",
  "GATEWAY_UNREACHABLE",
  "GATEWAY_RESPONSE_INVALID",
  "LOCAL_RESPONSE_INVALID",
  "OPERATOR_IDENTITY_CHANGED",
]);

export const COACH_OPERATION_RECOVERY_STORAGE_KEY = "billiard-erp:coach-operation-recovery:v1";

let pendingRecovery: CoachOperationRecovery | null = null;
let hydrated = false;
let recoveryUnavailable = false;

export function isCoachRuntimeOperationCommand(command: {
  kind: string;
}): command is CoachRuntimeOperationCommand {
  return ["CHECK_IN", "CHECK_OUT", "QUEUE", "TRANSITION", "ASSIGN"].includes(command.kind);
}

export function isCoachConfigurationSaveCommand(command: {
  kind: string;
}): command is CoachConfigurationSaveCommand {
  return ["SAVE_LEVEL", "SAVE_RULE", "SAVE_PRICE"].includes(command.kind);
}

export function isCoachRecoverableOperationCommand(command: {
  kind: string;
}): command is CoachRecoverableOperationCommand {
  return isCoachRuntimeOperationCommand(command) || isCoachConfigurationSaveCommand(command);
}

export function coachOperationIdentityKey(operator: {
  tenantId: string;
  storeId: string;
  id: string;
}): string {
  return JSON.stringify([operator.tenantId, operator.storeId, operator.id]);
}

function hasExactKeys(record: Record<string, unknown>, keys: readonly string[]): boolean {
  return (
    Object.keys(record).length === keys.length && keys.every((key) => Object.hasOwn(record, key))
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function isBoundedString(value: unknown, minimum: number, maximum: number): value is string {
  return typeof value === "string" && value.length >= minimum && value.length <= maximum;
}

function isReason(value: unknown): value is string | null {
  return value === null || isBoundedString(value, 2, 500);
}

function isIdempotencyKey(value: unknown): value is string {
  return isBoundedString(value, 8, 128);
}

function isIdentityKey(value: unknown): value is string {
  if (!isBoundedString(value, 1, 1_024)) return false;
  try {
    const identity = JSON.parse(value) as unknown;
    return (
      Array.isArray(identity) &&
      identity.length === 3 &&
      identity.every((part) => isBoundedString(part, 1, 256))
    );
  } catch {
    return false;
  }
}

function immutableValue<T>(value: T): T {
  if (Array.isArray(value)) {
    return Object.freeze(value.map((item) => immutableValue(item))) as T;
  }
  if (value !== null && typeof value === "object") {
    return Object.freeze(
      Object.fromEntries(
        Object.entries(value as Record<string, unknown>).map(([key, item]) => [
          key,
          immutableValue(item),
        ]),
      ),
    ) as T;
  }
  return value;
}

function immutableCommand(
  command: CoachRecoverableOperationCommand,
): CoachRecoverableOperationCommand {
  return immutableValue(command);
}

function immutableRecovery(
  ownerIdentityKey: string,
  command: CoachRecoverableOperationCommand,
  state: CoachOperationRecoveryState,
): CoachOperationRecovery {
  return Object.freeze({
    ownerIdentityKey,
    command: immutableCommand(command),
    state,
  });
}

function storedCommand(value: unknown): CoachRecoverableOperationCommand | null {
  if (!isRecord(value) || !isBoundedString(value.label, 1, 100)) return null;
  if (["SAVE_LEVEL", "SAVE_RULE", "SAVE_PRICE"].includes(String(value.kind))) {
    if (
      !hasExactKeys(value, ["kind", "configuration", "idempotencyKey", "label"]) ||
      !isCoachConfigurationSnapshot(value.configuration) ||
      !isIdempotencyKey(value.idempotencyKey)
    ) {
      return null;
    }
    return immutableCommand(value as unknown as CoachConfigurationSaveCommand);
  }
  if (value.kind === "CHECK_IN") {
    if (
      !hasExactKeys(value, [
        "kind",
        "coachId",
        ...(Object.hasOwn(value, "reason") ? ["reason"] : []),
        "idempotencyKey",
        "label",
      ]) ||
      !isBoundedString(value.coachId, 1, 256) ||
      (value.reason !== undefined &&
        (!isReason(value.reason) ||
          (typeof value.reason === "string" && value.reason !== value.reason.trim()))) ||
      !isIdempotencyKey(value.idempotencyKey)
    ) {
      return null;
    }
    return immutableCommand(value as unknown as CoachRuntimeOperationCommand);
  }
  if (value.kind === "CHECK_OUT") {
    if (
      !hasExactKeys(value, ["kind", "coachId", "reason", "idempotencyKey", "label"]) ||
      !isBoundedString(value.coachId, 1, 256) ||
      !isReason(value.reason) ||
      !isIdempotencyKey(value.idempotencyKey)
    ) {
      return null;
    }
    return immutableCommand(value as unknown as CoachRuntimeOperationCommand);
  }
  if (value.kind === "QUEUE") {
    if (!hasExactKeys(value, ["kind", "input", "label"]) || !isRecord(value.input)) return null;
    const input = value.input;
    const action = input.action;
    const actionAllowed = ["JOIN", "PAUSE", "RESUME", "SKIP", "PASS", "MOVE"].includes(
      typeof action === "string" ? action : "",
    );
    const targetAllowed =
      action === "MOVE"
        ? Number.isSafeInteger(input.targetPosition) && (input.targetPosition as number) >= 1
        : input.targetPosition === null;
    if (
      !hasExactKeys(input, ["coachId", "action", "targetPosition", "reason", "idempotencyKey"]) ||
      !isBoundedString(input.coachId, 1, 256) ||
      !actionAllowed ||
      !targetAllowed ||
      !isReason(input.reason) ||
      !isIdempotencyKey(input.idempotencyKey)
    ) {
      return null;
    }
    return immutableCommand(value as unknown as CoachRuntimeOperationCommand);
  }
  if (value.kind === "TRANSITION") {
    if (
      !hasExactKeys(value, ["kind", "serviceId", "action", "reason", "idempotencyKey", "label"]) ||
      !isBoundedString(value.serviceId, 1, 256) ||
      !["START", "COMPLETE", "END_EARLY", "CANCEL", "TERMINATE_ABNORMAL"].includes(
        typeof value.action === "string" ? value.action : "",
      ) ||
      !isReason(value.reason) ||
      !isIdempotencyKey(value.idempotencyKey)
    ) {
      return null;
    }
    return immutableCommand(value as unknown as CoachRuntimeOperationCommand);
  }
  if (value.kind === "ASSIGN") {
    const requestedCoach =
      value.assignmentType === "ROTATION"
        ? value.coachId === null
        : value.assignmentType === "REQUESTED" && isBoundedString(value.coachId, 1, 256);
    if (
      !hasExactKeys(value, [
        "kind",
        "sessionId",
        "coachId",
        "assignmentType",
        "serviceRuleId",
        ...(Object.hasOwn(value, "reason") ? ["reason"] : []),
        "idempotencyKey",
        "label",
      ]) ||
      !isBoundedString(value.sessionId, 1, 256) ||
      !requestedCoach ||
      !isBoundedString(value.serviceRuleId, 1, 256) ||
      (value.reason !== undefined &&
        (!isReason(value.reason) ||
          (typeof value.reason === "string" && value.reason !== value.reason.trim()))) ||
      !isIdempotencyKey(value.idempotencyKey)
    ) {
      return null;
    }
    return immutableCommand(value as unknown as CoachRuntimeOperationCommand);
  }
  return null;
}

function storedRecovery(value: unknown): CoachOperationRecovery | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["version", "recovery"]) ||
    value.version !== 1 ||
    !isRecord(value.recovery)
  ) {
    return null;
  }
  const recovery = value.recovery;
  if (
    !hasExactKeys(recovery, ["ownerIdentityKey", "command", "state"]) ||
    !isIdentityKey(recovery.ownerIdentityKey) ||
    (recovery.state !== "CLAIMED" && recovery.state !== "OUTCOME_UNKNOWN")
  ) {
    return null;
  }
  const command = storedCommand(recovery.command);
  if (!command) return null;
  return immutableRecovery(recovery.ownerIdentityKey, command, recovery.state);
}

function sessionStorageAccess(): StorageAccess {
  if (typeof window === "undefined") return { browser: false, storage: null };
  try {
    return {
      browser: true,
      storage: typeof sessionStorage === "undefined" ? null : sessionStorage,
    };
  } catch {
    return { browser: true, storage: null };
  }
}

function saveStoredRecovery(recovery: CoachOperationRecovery): boolean {
  const access = sessionStorageAccess();
  if (!access.browser) return true;
  if (!access.storage) return false;
  try {
    const encoded = JSON.stringify({ version: 1, recovery });
    access.storage.setItem(COACH_OPERATION_RECOVERY_STORAGE_KEY, encoded);
    const stored = access.storage.getItem(COACH_OPERATION_RECOVERY_STORAGE_KEY);
    return stored !== null && storedRecovery(JSON.parse(stored) as unknown) !== null;
  } catch {
    return false;
  }
}

function hydrateStoredRecovery(): void {
  if (hydrated) return;
  hydrated = true;
  const access = sessionStorageAccess();
  if (!access.browser) return;
  if (!access.storage) {
    recoveryUnavailable = true;
    return;
  }
  try {
    const encoded = access.storage.getItem(COACH_OPERATION_RECOVERY_STORAGE_KEY);
    if (encoded === null) return;
    const stored = storedRecovery(JSON.parse(encoded) as unknown);
    if (!stored) {
      recoveryUnavailable = true;
      return;
    }
    const recovery =
      stored.state === "CLAIMED"
        ? immutableRecovery(stored.ownerIdentityKey, stored.command, "OUTCOME_UNKNOWN")
        : stored;
    if (stored.state === "CLAIMED" && !saveStoredRecovery(recovery)) {
      recoveryUnavailable = true;
      return;
    }
    pendingRecovery = recovery;
  } catch {
    recoveryUnavailable = true;
  }
}

export function readCoachOperationRecovery(identityKey: string): CoachOperationRecoveryView {
  hydrateStoredRecovery();
  if (recoveryUnavailable) return { status: "UNAVAILABLE" };
  if (!pendingRecovery) return { status: "NONE" };
  if (pendingRecovery.ownerIdentityKey !== identityKey) return { status: "OTHER_OPERATOR" };
  return { status: "OWNED", recovery: pendingRecovery };
}

export function claimCoachOperationRecovery(
  identityKey: string,
  command: CoachRecoverableOperationCommand,
): CoachOperationRecoveryView {
  const current = readCoachOperationRecovery(identityKey);
  if (current.status !== "NONE") return current;
  const recovery = immutableRecovery(identityKey, command, "CLAIMED");
  if (!saveStoredRecovery(recovery)) {
    recoveryUnavailable = true;
    return { status: "UNAVAILABLE" };
  }
  pendingRecovery = recovery;
  return { status: "OWNED", recovery };
}

export function markCoachOperationOutcomeUnknown(
  recovery: CoachOperationRecovery,
  identityKey: string,
): boolean {
  if (
    recoveryUnavailable ||
    !pendingRecovery ||
    pendingRecovery !== recovery ||
    recovery.ownerIdentityKey !== identityKey
  ) {
    return false;
  }
  if (recovery.state === "OUTCOME_UNKNOWN") return true;
  const unknown = immutableRecovery(identityKey, recovery.command, "OUTCOME_UNKNOWN");
  if (!saveStoredRecovery(unknown)) {
    recoveryUnavailable = true;
    return false;
  }
  pendingRecovery = unknown;
  return true;
}

export function clearCoachOperationRecovery(
  recovery: CoachOperationRecovery,
  identityKey: string,
): boolean {
  if (
    recoveryUnavailable ||
    !pendingRecovery ||
    pendingRecovery !== recovery ||
    pendingRecovery.ownerIdentityKey !== identityKey ||
    recovery.ownerIdentityKey !== identityKey
  ) {
    return false;
  }
  const access = sessionStorageAccess();
  if (access.browser) {
    if (!access.storage) {
      recoveryUnavailable = true;
      return false;
    }
    try {
      access.storage.removeItem(COACH_OPERATION_RECOVERY_STORAGE_KEY);
      if (access.storage.getItem(COACH_OPERATION_RECOVERY_STORAGE_KEY) !== null) {
        recoveryUnavailable = true;
        return false;
      }
    } catch {
      recoveryUnavailable = true;
      return false;
    }
  }
  pendingRecovery = null;
  return true;
}

export function coachOperationOutcomeIsUnknown(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  const failure = error as RequestFailureShape;
  const code = typeof failure.code === "string" ? failure.code : undefined;
  const statusCode = typeof failure.statusCode === "number" ? failure.statusCode : undefined;
  if (code && unknownCoachOperationCodes.has(code)) return true;
  if (
    code &&
    (definitelyStoppedBeforeCoachOperation.has(code) || code.startsWith("AUTHORIZATION_"))
  ) {
    return false;
  }
  if (statusCode !== undefined && statusCode >= 400 && statusCode < 500 && statusCode !== 408) {
    return false;
  }
  return statusCode === 408 || (statusCode !== undefined && statusCode >= 500);
}

export function resetCoachOperationRecoveryForTests(): void {
  pendingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
  const access = sessionStorageAccess();
  try {
    access.storage?.removeItem(COACH_OPERATION_RECOVERY_STORAGE_KEY);
  } catch {
    // Test cleanup must also reset the in-memory fail-closed state.
  }
}

export function resetCoachOperationRecoveryMemoryForTests(): void {
  pendingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
}
